import moment from 'moment-timezone';
import {List, type Map} from 'immutable';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {isDateTimeField} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

type OptionValidation = Map<string, string>;

const EMPTY_VALUE_TEXT = '—';

const formatDate = (
  value: string,
  formatString: string,
  timezone?: string
): string => {
  let m = moment.utc(value);
  if (timezone) {
    m = m.tz(timezone);
  }
  return m.format(formatString);
};

/**
 * Formats a CMS item field value as row text for the curated item picker.
 * Modeled on the CMS panel table's cell rendering (`renderCellForField` in
 * CMSTableContextManager) but returns plain strings for compact rows.
 */
export const getFieldDisplayText = (
  field: FieldType,
  value: unknown,
  {timezone}: {timezone?: string} = {}
): string => {
  if (value == null || value === '') {
    return EMPTY_VALUE_TEXT;
  }

  switch (field.get('type')) {
    case 'Date':
      // Match the CMS panel table: only date-time values are converted to
      // the site timezone. Date-only values are stored as UTC midnight, so
      // converting them would render the previous calendar day west of UTC.
      return isDateTimeField(field)
        ? formatDate(String(value), 'MMM D, YYYY h:mm A', timezone)
        : formatDate(String(value), 'MMM D, YYYY');
    case 'Option': {
      const options = field.getIn(
        ['validations', 'options'],
        List()
      ) as List<OptionValidation>;
      // Option definitions key their identifier as `id` (older data may use
      // `_id`) — mirror RecordUtils.getId's `_id || id` fallback.
      const option = options.find(
        (candidate) => (candidate.get('_id') ?? candidate.get('id')) === value
      );
      return option ? option.get('name', EMPTY_VALUE_TEXT) : EMPTY_VALUE_TEXT;
    }
    case 'Bool':
      return value ? 'Yes' : 'No';
    default:
      return String(value);
  }
};
